import * as React from 'react';
import {useEffect, useMemo, useState} from 'react';
import isEmpty from 'lodash/isEmpty';
import Highcharts from 'highcharts';
import clsx from 'clsx';

import {
  generatePointTooltipParams,
  getLineChartData,
  getLineSeries,
  getOtherSegmentData,
  OtherSegmentData,
} from 'common/report/insights/charts/insights-line-chart/util';
import {PositionParent} from 'common/components/drop-menu';
import {IChartDataItem, LineChartYAxisOptions, XAxisOptions} from 'common/charts/visualization/line-chart/types';
import {ParamsAndResults} from 'common/report/types';
import {InsightsQueryResults} from 'common/report/insights/util/types';
import {ChartType, InsightsBookmarkParams, SortConfig} from 'common/types/reports/bookmark';
import {PopoverCallback} from 'common/charts/types';
import * as BookmarkTypes from 'common/types/reports/bookmark';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {isPublicMode} from 'common/report/util';
import {dataGroupStore} from 'common/report/stores/data-groups';
import {canShowOtherSegment, getSortConfig, insightsBookmarkParamsToChartParams} from 'common/report/insights/util';
import {INSIGHTS_LINE_CSV_EXPORT_LIMIT, REPORT_LIMITS, ReportName} from 'common/report/constants';
import {isCustomCsvLimitEnabled} from 'common/widgets/csv-export-modal/utils';
import {
  renderLineChartTooltip,
  RenderLineChartTooltipParams,
  ShowClauseData,
} from 'common/report/insights/util/tooltip';
import {OTHER_SEGMENT_VALUE} from 'common/report/insights/util/constants';
import {useMetrics} from 'common/contexts/react/metrics-resource-context';
import {deselectPoint, selectPoint} from 'common/report/util/highcharts';
import {
  getDisplayOptionsFromLineChartYAxisOptions,
  getMetricFormattingDefaultsFromChartParams,
  mergeDisplayOptions,
  metricFormatterFunctionForAxis,
} from 'common/report/insights/charts/util';
import {
  getDefaultPrimaryAndSecondaryAxisOptions,
  getYAxisDefaultOptions,
} from 'common/query-builder/chart-customization/utils';
import {formatMetricNumberWithUnits} from 'common/util';
import {SpinnerReact} from 'common/components/spinner';
import {colors} from 'common/util/colors';
import {TimeSeriesType} from 'common/charts/visualization/line-chart/enums';
import {renamePropertyValue} from 'common/report/util/properties';
import {ContextMenuReact} from 'common/widgets/context-menu';
import {useTranslation} from 'common/i18n/hooks';
import {SegmentLegendHeader} from 'common/charts/misc/segment-legend-header';
import {LineChartReact} from 'common/charts/visualization/line-chart';
import {isRetentionOnlyInsightsBookmark} from 'common/report/insights/models/utils';
import {EmptyDataReact} from 'common/charts/misc/empty-data';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {SegmentFilterController} from 'common/charts/contexts/segment-filter-controller';
import {IronContextRegistry} from 'common/report/context-registry';
import {withStyles} from 'common/util/react';

import styles from './index.module.styl';
import css from './index.module.styl?inline';

const LEFT_MARGIN_WITHOUT_YAXIS = 25;

export type OnPopoverActionProps = {detail: {action: string; pointData: RenderLineChartTooltipParams | null}};

export type InsightsLineChartProps = {
  elref?: string;

  allowChartUpdate?: boolean;
  allowTooltipOutsideChart?: boolean;

  segmentFilterController: SegmentFilterController;

  // use a single prop for bookmark params and results so they can be updated together atomically
  paramsAndResults: ParamsAndResults<InsightsBookmarkParams, InsightsQueryResults> | null;
  popoverCallback?: PopoverCallback;
  dataGroups?: Record<string, DataGroup>;
  formatXAxisLabel?: (value: number, timeUnit?: TimeUnit) => string;

  disableTooltip?: boolean;
  disableZoom?: boolean;
  failed?: boolean;
  hideChartLines?: boolean;
  hideLegend?: boolean;
  hideYAxis?: boolean;
  loaded: boolean;
  canShowAnnotations?: boolean;
  showFirstLastTick?: boolean;
  timezone?: string;
  utcOffset?: number;
  xAxisOptions?: XAxisOptions;
  primaryYAxisOptions?: Highcharts.YAxisOptions;
  secondaryYAxisOptions?: Highcharts.YAxisOptions;
  emptyDataContainerClassName?: string;

  onPopoverAction?: (props: OnPopoverActionProps) => void;
  onHighchartMouseOver?: ({
    tooltipParams,
    chartContainer,
    plotLeft,
    plotTop,
    plotX,
    plotY,
  }: {
    tooltipParams: RenderLineChartTooltipParams;
    chartContainer: DOMRect;
    plotLeft: number;
    plotTop: number;
    plotX: number;
    plotY: number;
  }) => void;
  onHighchartMouseOut?: () => void;
};

/**
 * Responsibilities:
 *
 * 1. transform raw insight query to LinChartData data shape
 * 2. manage segmentFilterController state by parsing `legend` to select correct segments
 */
export const InsightsLineChart: React.FC<
  Pick<IronContextRegistry, `currentProject` | `i18n` | `metrics`> & InsightsLineChartProps
> = (props) => {
  /** clicked highcharts point object to display a popover for */
  const [popoverSelection, setPopoverSelection] = useState<Highcharts.Point | null>(null);
  /** holds positioning of the popover for the active highcharts point */
  const [popoverPositionParent, setPopoverPositionParent] = useState<PositionParent>();
  const bookmarkParams = props.paramsAndResults?.params;
  const results = props.paramsAndResults?.results;

  /** utility state to force rerender when segmentFilterController changes */
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    const callback = () => forceUpdate((x) => x + 1);

    props.segmentFilterController.subscribe(callback);
    return () => {
      props.segmentFilterController.unsubscribe(callback);
    };
  }, [props.segmentFilterController, forceUpdate]);

  const currentProject = isPublicMode() ? undefined : props.currentProject.get().value;
  const projectTimezone = useMemo(() => {
    return props.timezone
      ? {
          utcOffset: props.utcOffset!,
          timezone: props.timezone,
        }
      : // if props.utcOffset & props.timezone are not provided, then currentProject must be defined (non-public mode)
        // in public mode, we need to explicitly pass in utcOffset & timezone as props
        {
          utcOffset: currentProject?.utcOffset || 0,
          timezone: currentProject?.timezone || `UTC`,
        };
  }, [props.timezone, props.utcOffset, currentProject]);

  const dataGroups = useMemo(() => (props.dataGroups || dataGroupStore.value) ?? {}, [props.dataGroups]);
  const lineChartData = useMemo(() => {
    if (bookmarkParams && results && props.metrics) {
      return getLineChartData(
        projectTimezone,
        // extracting only the actual values used allows for better memoization
        insightsBookmarkParamsToChartParams(bookmarkParams),
        results,
        dataGroups,
        props.metrics,
        props.i18n,
      );
    }
    return null;
  }, [projectTimezone, bookmarkParams, results, dataGroups, props.metrics, props.i18n]);

  const isEndDateIncomplete = () => {
    if (!lineChartData?.lineChartSeries.length) {
      return false;
    }
    if (!bookmarkParams) {
      return false;
    }
    const hasIncompleteData = lineChartData?.lineChartSeries[0].incompleteLength ?? 0;
    return isRetentionOnlyInsightsBookmark(bookmarkParams) && hasIncompleteData > 0;
  };

  const hasForecastComparison = (lineChartData?.forecastComparisonSegmentLookup?.size ?? 0) > 0;

  // load segment filter options
  useEffect(() => {
    if (lineChartData && results && bookmarkParams) {
      const {meta} = results;
      const {apiResultsTruncated, queryLimits} = meta;

      props.segmentFilterController.loadSections(
        [
          {
            items: lineChartData.legendSegmentItems,
            metadata: lineChartData.legendIdToIndexMap, // Insights app is expected to use this mapping to persist legend selection indices to the URL
            apiResultsTruncated,
            tableColumns: lineChartData.tableColumns,
            readOnlyItems: lineChartData.readOnlyLegendItems,
          },
        ],
        {
          defaultNumSelections:
            REPORT_LIMITS[ReportName.Insights].segments[
              bookmarkParams.displayOptions.chartType as ChartType.Line | ChartType.Column
            ].default,
          maxNumSelections:
            REPORT_LIMITS[ReportName.Insights].segments[
              bookmarkParams.displayOptions.chartType as ChartType.Line | ChartType.Column
            ].max,
          tableSortConfig: getSortConfig({
            bookmarkSortConfig: bookmarkParams.sorting || undefined,
            headerCount: results.headers.length,
          })[ChartType.Line] as SortConfig, // Column chart shares sort config with line
        },
      );

      props.segmentFilterController.segmentationQueryLimit = queryLimits?.limit ?? 0;
      props.segmentFilterController.exportSegmentationQueryLimit = isCustomCsvLimitEnabled(currentProject)
        ? INSIGHTS_LINE_CSV_EXPORT_LIMIT
        : REPORT_LIMITS[ReportName.Insights].csvExport;
    }
  }, [lineChartData, results, bookmarkParams]);

  // update segment filter selections if the legend selections changed
  useEffect(() => {
    const individualSelectedSegments = bookmarkParams?.legend?.individualSelectedSegments;

    if (individualSelectedSegments) {
      props.segmentFilterController.loadFromSavedLegend(
        individualSelectedSegments ? [individualSelectedSegments] : null,
      );
    }
  }, [bookmarkParams?.legend?.individualSelectedSegments]);

  const loaded = props.loaded && !!(bookmarkParams && results && props.metrics) && lineChartData;
  if (!loaded) {
    return (
      <div className={styles.root}>
        <SpinnerReact className={styles.mpSpinner} elref={`loadingSpinner`} />
      </div>
    );
  }

  if (isEmpty(results?.series)) {
    return (
      <div className={`${styles.root} ${props.emptyDataContainerClassName}`}>
        <EmptyDataReact />
      </div>
    );
  }

  const formatYAxisLabel = (
    y: number,
    axis: BookmarkTypes.AxisAssignment,
    yAxisOptions?: LineChartYAxisOptions,
  ): string => {
    if (!bookmarkParams) {
      return ``;
    }
    const defaultOptions = getMetricFormattingDefaultsFromChartParams(bookmarkParams, true);
    const {defaultPrimaryOptions, defaultSecondaryOptions} = getDefaultPrimaryAndSecondaryAxisOptions(
      bookmarkParams,
      props.metrics,
    );

    const preFormatted = metricFormatterFunctionForAxis(bookmarkParams, axis)(y);
    const formatted = formatMetricNumberWithUnits(
      preFormatted,
      mergeDisplayOptions(
        {},
        defaultOptions,
        getDisplayOptionsFromLineChartYAxisOptions(
          axis === BookmarkTypes.AxisAssignment.Primary ? defaultPrimaryOptions : defaultSecondaryOptions,
        ),
        getDisplayOptionsFromLineChartYAxisOptions(yAxisOptions),
      ),
      {abbreviateThousands: true},
    );
    return formatted;
  };

  // There will be multiple segments with the same segment name when using time comparison
  const seriesLookup = new Map<string, IChartDataItem[]>();
  lineChartData.lineChartSeries.forEach((segment) => {
    const segmentKey = segment.name;
    if (!seriesLookup.has(segmentKey)) {
      seriesLookup.set(segmentKey, []);
    }
    seriesLookup.get(segmentKey)!.push(segment);
  });

  const visibleChartSeries: IChartDataItem[] = props.segmentFilterController.getSelectedItems(0).flatMap((item) => {
    return (
      seriesLookup.get(item.id)?.map((segment) => ({
        ...segment,
        color: props.segmentFilterController.getItemColor(0, item.id),
      })) || []
    );
  });

  // include other segment in rendering cycle rather than data processing cycle
  // so that we have accurate values on each segment selection
  let otherSegmentData: OtherSegmentData;
  if (canShowOtherSegment(bookmarkParams)) {
    const allSegments = Array.from(lineChartData.segmentLookup.values());

    otherSegmentData = getOtherSegmentData(allSegments, props.segmentFilterController);
    if (!otherSegmentData.isAllZero) {
      visibleChartSeries.unshift(
        getLineSeries(
          otherSegmentData.percentagesSegment,
          colors.otherSegment.primary,
          projectTimezone!,
          results!.meta.report_sections.show![0],
        ),
      );
    }
  }

  const getPointDataFromSelection = () => {
    if (popoverSelection === null) {
      return null;
    }
    const point = popoverSelection;
    return generatePointTooltipParams({
      highchartsContext: {point, series: point.series, percentage: point.percentage ?? 0},
      bookmarkParams,
      results,
      lineChartData,
      dataGroups: Object.values(dataGroups),
      otherSegmentData,
      projectTimezone,
      metrics: props.metrics,
      i18n: props.i18n,
    });
  };

  const handlePopoverClose = () => {
    const selectedPoint = popoverSelection;
    if (selectedPoint) {
      deselectPoint(selectedPoint);
    }
    setPopoverSelection(null);
    setPopoverPositionParent(undefined);
  };

  const handlePopoverAction = (action: string) => {
    props.onPopoverAction?.({detail: {action, pointData: getPointDataFromSelection()}});
    handlePopoverClose();
  };

  const handleHighchartMouseOver = ({
    highchartPoint,
    highchartChart,
  }: {
    highchartPoint: Highcharts.Point;
    highchartChart: Highcharts.Chart;
  }) => {
    const tooltipParams = generatePointTooltipParams({
      highchartsContext: {
        point: highchartPoint,
        series: highchartPoint.series,
        percentage: highchartPoint.percentage ?? 0,
      },
      bookmarkParams,
      results,
      lineChartData,
      dataGroups: Object.values(dataGroups),
      projectTimezone,
      otherSegmentData,
      metrics: props.metrics,
      i18n: props.i18n,
    });
    props.onHighchartMouseOver?.({
      tooltipParams,
      chartContainer: highchartChart.container.getBoundingClientRect(), // container’s on‐screen position
      plotLeft: highchartChart.plotLeft,
      plotTop: highchartChart.plotTop,
      plotX: highchartPoint.plotX!,
      plotY: highchartPoint.plotY!,
    });
  };

  const handleHighchartMouseOut = () => {
    props.onHighchartMouseOut?.();
  };

  const getPopoverMenuItems = () => {
    const pointData = getPointDataFromSelection();
    if (!pointData || !popoverSelection) {
      return [];
    }
    const metricKey = (pointData.metricClauseData as ShowClauseData).metricKey;
    return props.popoverCallback?.(metricKey, pointData.segments, {
      highchartsPointContext: popoverSelection,
      isTimeComparisonSegment: pointData.isComparisonItem,
      pointData,
    });
  };

  const visibleSegmentIds = visibleChartSeries.map((segment) => {
    return segment.name;
  });

  const bookmarkPrimaryYAxisOptions = bookmarkParams.displayOptions.primaryYAxisOptions;

  const metricsResource = props.metrics;

  const defaultPrimaryYAxisOptions = getYAxisDefaultOptions(
    bookmarkParams,
    BookmarkTypes.AxisAssignment.Primary,
    metricsResource,
  );
  const defaultSecondaryYAxisOptions = getYAxisDefaultOptions(
    bookmarkParams,
    BookmarkTypes.AxisAssignment.Secondary,
    metricsResource,
  );

  const bookmarkSecondaryYAxisOptions = bookmarkParams.displayOptions.secondaryYAxisOptions;
  const bookmarkXAxisOptions = bookmarkParams.displayOptions.xAxisOptions;

  // Arrow syntax is required to bind "this", which is otherwise changed by highcharts
  const handlePointClick = (point: Highcharts.Point, event?: Highcharts.PointClickEventObject) => {
    const {series} = point;
    if (series.options.name === OTHER_SEGMENT_VALUE) {
      return;
    }

    const pointData = generatePointTooltipParams({
      highchartsContext: {point, series, percentage: point.percentage!},
      bookmarkParams,
      results,
      lineChartData,
      dataGroups: Object.values(dataGroups),
      projectTimezone: projectTimezone!,
      otherSegmentData,
      metrics: props.metrics,
      i18n: props.i18n,
    });
    const metricKey = (pointData.metricClauseData as ShowClauseData).metricKey;
    const hasPopoverActions = props.popoverCallback?.(metricKey, pointData.segments, {
      highchartsPointContext: point,
      pointData,
    }).length;

    if (hasPopoverActions) {
      selectPoint(point);
      setPopoverPositionParent({
        getBoundingClientRect: () => {
          const isVerticalBar = bookmarkParams.displayOptions.chartType === ChartType.Column;
          const targetEl: HTMLElement =
            point.graphic?.element ?? (point.series as any).markerGroup?.element?.firstChild;
          const targetRect = targetEl?.getBoundingClientRect();

          // close the popover when the line chart is resized and the selected point marker goes away
          if (targetRect === undefined) {
            handlePopoverClose();
            return new DOMRect(0, 0, 0, 0);
          } else if (isVerticalBar) {
            // makes sure the popover shifts over the Y axis to align it with the mouse cursor
            // this improves accessing the menu on larger columns
            return new DOMRect(targetRect.left, event!.pageY, targetRect.width, targetRect.height);
          }

          return targetRect;
        },
      });
      setPopoverSelection(point);
    }
  };

  return (
    <div className={clsx(styles.root, `insights-line-chart`)} elref={props.elref}>
      {!props.hideLegend && (
        <SegmentLegendHeader
          className={styles.segmentLegendHeader}
          visibleSegmentIds={visibleSegmentIds}
          segmentFilterController={props.segmentFilterController}
        />
      )}
      <LineChartReact
        className={styles.lineChart}
        allowChartUpdate={props.allowChartUpdate}
        allowTooltipOutsideChart={props.allowTooltipOutsideChart}
        segmentFilterController={props.segmentFilterController}
        chartData={visibleChartSeries}
        chartMarginLeft={props.primaryYAxisOptions?.visible === false ? LEFT_MARGIN_WITHOUT_YAXIS : undefined}
        displayAnalysisType={lineChartData.chartAnalysisType}
        displayPlotStyle={lineChartData.chartPlotStyle}
        displaySeriesType={
          bookmarkParams.displayOptions.chartType === ChartType.Column ? TimeSeriesType.Column : TimeSeriesType.Line
        }
        displayStackingType={lineChartData.chartStackingType}
        disableAreaRangeMouseTracking={hasForecastComparison}
        {...(props.showFirstLastTick && {
          formatTicksX: (min, max) => {
            return [min, max];
          },
        })}
        disableTooltip={props.disableTooltip}
        formatTooltip={(_x, _y, _series, _seriesIdx, tooltipContext): string => {
          const tooltipParams = generatePointTooltipParams({
            highchartsContext: tooltipContext,
            bookmarkParams,
            results,
            lineChartData,
            dataGroups: Object.values(dataGroups),
            projectTimezone: projectTimezone!,
            otherSegmentData,
            metrics: props.metrics,
            i18n: props.i18n,
          });
          return renderLineChartTooltip({
            ...tooltipParams,
            segments: tooltipParams.segments.map((value, index) =>
              renamePropertyValue(value, results.headers[index + 1], bookmarkParams.sections.group![index]),
            ),
            timezone: projectTimezone?.timezone,
            i18n: props.i18n,
            metricsResource: props.metrics,
          });
        }}
        formatYAxisLabel={formatYAxisLabel}
        hideChartLines={props.hideChartLines}
        incompleteToDate={isEndDateIncomplete()}
        onHighChartClick={props.popoverCallback && ((a, b) => handlePointClick(a, b))}
        canShowAnnotations={props.canShowAnnotations}
        hideAnnotations={!!bookmarkParams.displayOptions.annotationOptions?.hideAnnotations}
        annotationTagFilterIds={bookmarkParams.displayOptions.annotationOptions?.tagFilterIds || undefined}
        showUntagged={bookmarkParams.displayOptions.annotationOptions?.showUntagged || undefined}
        annotationsSort={bookmarkParams.displayOptions.annotationOptions?.sortOrder || undefined}
        timeUnit={lineChartData.chartTimeUnit}
        timezone={projectTimezone?.timezone}
        utcOffset={projectTimezone?.utcOffset}
        defaultPrimaryYAxisOptions={defaultPrimaryYAxisOptions}
        defaultSecondaryYAxisOptions={defaultSecondaryYAxisOptions}
        primaryYAxisOptions={{
          ...bookmarkPrimaryYAxisOptions,
          ...props.primaryYAxisOptions,
        }}
        secondaryYAxisOptions={{
          ...bookmarkSecondaryYAxisOptions,
          ...props.secondaryYAxisOptions,
        }}
        zoomType={!props.disableZoom ? `x` : undefined}
        xAxisOptions={{
          ...props.xAxisOptions,
          ...bookmarkXAxisOptions,
        }}
        {...(props.formatXAxisLabel && {
          formatXAxisLabel: props.formatXAxisLabel,
        })}
        onHighchartMouseOver={handleHighchartMouseOver}
        onHighchartMouseOut={handleHighchartMouseOut}
      />
      {/* render point popover */}
      {popoverSelection && (
        <>
          <ContextMenuReact
            className={styles.mpContextMenu}
            relativePositioning
            open
            position={{left: 0, top: 0}}
            onClose={() => handlePopoverClose()}
            onAction={(ev) => handlePopoverAction(ev.detail.action)}
            positionParent={popoverPositionParent}
            menuItems={getPopoverMenuItems()}
          />
        </>
      )}
    </div>
  );
};

export const InsightsLineChartReact = withStyles((props: InsightsLineChartProps) => {
  const currentProject = useCurrentProject();
  const i18n = useTranslation();
  const metrics = useMetrics();
  return <InsightsLineChart currentProject={currentProject} i18n={i18n} metrics={metrics} {...props} />;
}, css);
